import type React from "react";
import { useTranslation } from "../hooks/useTranslation";

export const About: React.FC = () => {
	const t = useTranslation();

	return (
		<div className="space-y-4 mt-4">
			<div>
				<h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">
					{t.about.overview.title}
				</h3>
				<p className="text-sm text-gray-600 dark:text-gray-300">
					{t.about.overview.content}
				</p>
			</div>

			<div>
				<h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">
					{t.about.features.title}
				</h3>
				<ul className="list-disc pl-5 text-sm space-y-1 text-gray-600 dark:text-gray-300">
					{t.about.features.items.map((item) => (
						<li key={item.substring(0, 10)}>{item}</li>
					))}
				</ul>
			</div>

			<div>
				<h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">
					{t.about.tech.title}
				</h3>
				<ul className="list-disc pl-5 text-sm space-y-1 text-gray-600 dark:text-gray-300">
					{t.about.tech.items.map((item) => (
						<li key={item.substring(0, 10)}>{item}</li>
					))}
				</ul>
			</div>

			<div>
				<h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">
					{t.about.contact.title}
				</h3>
				<div className="text-sm space-y-2 text-gray-600 dark:text-gray-300">
					<p>
						<a
							href="https://nextint.co.jp/"
							target="_blank"
							rel="noopener noreferrer"
							className="hover:underline text-blue-600 dark:text-blue-400"
						>
							{t.about.contact.website}
						</a>
					</p>
					<p>
						<a
							href="https://x.com/tokoroten"
							target="_blank"
							rel="noopener noreferrer"
							className="hover:underline text-blue-600 dark:text-blue-400"
						>
							{t.about.contact.social}
						</a>
					</p>
				</div>
			</div>
		</div>
	);
};

// For creating a collapsible section
export interface CollapsibleSectionProps {
	title: string;
	icon: React.ReactNode;
	children: React.ReactNode;
	isOpen: boolean;
	onToggle: () => void;
	id: string;
	className?: string;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
	title,
	icon,
	children,
	isOpen,
	onToggle,
	id,
	className = "",
}) => {
	return (
		<div
			className={`rounded-xl border shadow-md bg-white border-gray-200 text-gray-800 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100 ${className} ${isOpen ? 'relative z-10' : ''}`}
		>
			<button
				type="button"
				className="w-full flex items-center justify-between rounded-t-xl border-b px-6 py-4 focus:outline-none"
				onClick={onToggle}
				aria-expanded={isOpen}
				aria-controls={`${id}-panel`}
				style={{ cursor: "pointer", background: "none", border: "none" }}
			>
				<h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
					<span className="mr-2">{icon}</span>
					{title}
				</h2>
				<span>{isOpen ? "▲" : "▼"}</span>
			</button>
			<div
				id={`${id}-panel`}
				className={`overflow-hidden transition-all duration-300 ease-in-out ${
					isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
				}`}
				style={{
					position: isOpen ? 'relative' : 'static',
					zIndex: isOpen ? 5 : 'auto'
				}}
			>
				<div className="p-6 bg-white text-gray-800 dark:bg-gray-900 dark:text-gray-100">
					{children}
				</div>
			</div>
		</div>
	);
};

export default About;
